import {
  SET_CACHE_AUTH,
  LOGOUT,
  AUTO_LOGOUT,
  SET_ACTIVE_CACHE_ID,
  SET_ACTIVE_PREFIX_ID,
  SET_MOBILE_MENU_OPEN,
  SET_TIME_RANGE,
  SET_LAYOUT,
  SET_PROMO_BANNER_DISMISSED,
} from "@/store/mutation-types";
import { State } from "@/store/state";
import router from "@/router/router-ANALYTICS";

import { RootState } from "@/types/root-state";
import { TimeRange } from "@/types/time-range";
import { RequestMethod } from "@/types/request-method";
import { Layout } from "@/types/layout";
import { ConsultationRequest } from "@/types/consultation";

import * as Msg from "@/config/notification";

import { setNotificationSuccess, setNotificationError } from "@/utils/notification";
import { isAdminUsingProd } from "@/utils/admin";
import { decodeJWT } from "@/utils/jwt";

import CacheService from "@/services/CacheService";

import Vue from "vue";
import Vuex from "vuex";

import { stats } from "@/store/modules/stats";
import { settings } from "@/store/modules/settings";
import { processedStats } from "@/store/modules/processed-stats";
import { notifications } from "@/store/modules/notifications";
import { alerts } from "@/store/modules/alerts";
import { debugMode } from "@/store/modules/debug-mode";
import { caches } from "@/store/modules/cachesight/caches";
import { Login } from "@/types/login";

Vue.use(Vuex);

/*
From component with method, do this.$store.dispatch('actionName')

state (store data)
mutations (commit and track state changes)
actions (best practice: have actions call mutations which update state)
getters (computed, for accessing state)
*/

const store = new Vuex.Store<RootState>({
  modules: {
    stats,
    settings,
    processedStats,
    notifications,
    alerts,
    debugMode,
    caches,
  },
  state: new State(),
  mutations: {
    [SET_CACHE_AUTH]: (state, token: string) => {
      state.token = token;
      localStorage.setItem("token", token);
    },
    // `LOGOUT` mutations are CacheSight only, but keeping here as auth is shared with Analytics
    [LOGOUT]: () => {
      localStorage.removeItem("token");
      location.reload();
    },
    [AUTO_LOGOUT]: () => {
      // Do not perform on failed login or password change
      if (router.currentRoute.name === "login" || router.currentRoute.name === "password") {
        return;
      }

      localStorage.removeItem("token");
      location.reload();
    },
    [SET_ACTIVE_CACHE_ID]: (state, cacheID: number) => {
      state.activeCacheID = cacheID;
    },
    [SET_ACTIVE_PREFIX_ID]: (state, prefixID: number) => {
      state.activePrefixID = prefixID;
    },
    [SET_MOBILE_MENU_OPEN]: (state, toggleState: boolean) => {
      state.mobileMenuOpen = toggleState;
    },
    [SET_TIME_RANGE]: (state, timeRange: TimeRange) => {
      state.timeRange = timeRange;
    },
    [SET_LAYOUT]: (state, layout: Layout) => {
      state.layout = layout;
    },
    [SET_PROMO_BANNER_DISMISSED]: (state, dismissed: boolean) => {
      state.promoBannerDismissed = dismissed;
    },
  },
  actions: {
    // Analytics login
    login({ commit, dispatch }, login: Login) {
      return CacheService.request(`login`, RequestMethod.POST, {}, {}, login)
        .then(response => {
          interface LoginResponse {
            cache_id: number;
            token: string;
          }
          const loginResponse: LoginResponse = response.data;

          commit(SET_CACHE_AUTH, loginResponse.token);
          commit(SET_ACTIVE_CACHE_ID, loginResponse.cache_id.toString());

          return loginResponse.cache_id;
        })
        .catch(error => {
          setNotificationError(dispatch, `${Msg.LOGIN_ERROR} (${error})`);
          return false;
        });
    },
    // CacheSight logout
    logout({ commit }) {
      return commit(LOGOUT);
    },
    autoLogout({ commit }) {
      return commit(AUTO_LOGOUT);
    },
    flushCache({ dispatch, rootState }) {
      if (isAdminUsingProd()) return;
      const cacheID = rootState.activeCacheID;
      return CacheService.request(`caches/${cacheID}/flush`, RequestMethod.POST)
        .then(() => {
          dispatch("stats/fetch");
        })
        .catch(error => {
          setNotificationError(dispatch, `${Msg.FLUSH_CACHE_ERROR} (${error})`);
        });
    },
    switchCluster({ dispatch, rootState }) {
      if (isAdminUsingProd()) return;
      const cacheID = rootState.activeCacheID;
      return CacheService.request(`caches/${cacheID}/switch_cluster`, RequestMethod.POST)
        .then(() => {
          dispatch("settings/fetch");
        })
        .catch(error => {
          setNotificationError(dispatch, `${Msg.SWITCH_CLUSTER_ERROR} (${error})`);
        });
    },
    reEnableCache({ dispatch, rootState }) {
      const cacheID = rootState.activeCacheID;
      return CacheService.request(`caches/${cacheID}/enable_cache`, RequestMethod.POST)
        .then(() => {
          dispatch("settings/fetch");
          setNotificationSuccess(dispatch, Msg.RE_ENABLE_CACHE_SUCCESS);
        })
        .catch(error => {
          setNotificationError(dispatch, `${Msg.RE_ENABLE_CACHE_ERROR} (${error})`);
        });
    },
    rotateSSO({ commit, dispatch, rootState }) {
      if (isAdminUsingProd()) return;
      const cacheID = rootState.activeCacheID;
      return CacheService.request(`caches/${cacheID}/rotate_sso`, RequestMethod.POST)
        .then(response => {
          const token: string = response.data;
          commit(SET_CACHE_AUTH, token);
        })
        .catch(error => {
          setNotificationError(dispatch, `${Msg.ROTATE_SSO_ERROR} (${error})`);
        });
    },
    setActiveCacheID({ commit }, cacheID: number) {
      commit(SET_ACTIVE_CACHE_ID, cacheID);
    },
    setActivePrefixID({ commit }, prefixID: string) {
      commit(SET_ACTIVE_PREFIX_ID, prefixID);
    },
    toggleMobileMenu({ commit }, toggleState: boolean) {
      commit(SET_MOBILE_MENU_OPEN, toggleState);
    },
    setTimeRange({ commit }, timeRange: TimeRange) {
      commit(SET_TIME_RANGE, timeRange);
    },
    setLayout({ commit }, layout: Layout) {
      commit(SET_LAYOUT, layout);
    },
    setPromoBannerDismissed({ commit }, dismissed: boolean) {
      commit(SET_PROMO_BANNER_DISMISSED, dismissed);
    },
    sendConsultationRequest({ commit, dispatch, rootState }, request: ConsultationRequest) {
      const cacheID = rootState.activeCacheID;
      return CacheService.request(`caches/${cacheID}/consulting`, RequestMethod.POST, {}, request)
        .then(() => {
          commit(SET_PROMO_BANNER_DISMISSED, true);
          setNotificationSuccess(dispatch, Msg.CONSULTATION_REQUEST_SUCCESS);
        })
        .catch(error => {
          setNotificationError(dispatch, `${Msg.CONSULTATION_REQUEST_ERROR} (${error})`);
        });
    },
  },
  getters: {
    provider: state => {
      const decodedJWT = decodeJWT(state.token);
      return decodedJWT && decodedJWT.provider ? decodedJWT.provider : "";
    },
    loggedIn: state => {
      return !!state.token;
    },
  },
});

export default store;
